// src/services/supplierNotificationService.js
import { collection, addDoc, query, where, getDocs, Timestamp, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Create a supplier order notification when a delivery task is created
 */
export const createSupplierOrderNotification = async (taskData, pickupData, vendorProfile) => {
  try {
    if (!pickupData.supplierEmail) {
      console.log('No supplier email provided, skipping notification');
      return null;
    }

    // Check if supplier has an account (by email)
    const suppliersQuery = query(
      collection(db, 'vendors'),
      where('email', '==', pickupData.supplierEmail)
    );
    const supplierSnapshot = await getDocs(suppliersQuery);
    
    let supplierId = null;
    if (!supplierSnapshot.empty) {
      supplierId = supplierSnapshot.docs[0].id;
    }

    // Create order in the 'orders' collection so it shows in Customer Orders
    const orderData = {
      // Standard order fields
      vendorId: supplierId, // Supplier's vendor ID - this is key!
      customerId: taskData.vendorId, // The vendor who created the task
      customerName: vendorProfile?.shopName || vendorProfile?.businessName || 'Vendor',
      customerEmail: vendorProfile?.email || '',
      customerPhone: vendorProfile?.phone || '',
      
      // Shipping/delivery info
      shippingAddress: {
        street: taskData.delivery?.address || '',
        landmark: taskData.delivery?.landmark || '',
        city: '',
        state: '',
        zip: '',
        country: 'India'
      },
      
      // Items from this pickup
      items: pickupData.products.map(p => ({
        productId: p.id,
        name: p.name,
        partNumber: p.partNumber,
        quantity: p.quantity,
        price: p.price,
        imageUrl: p.imageUrl || null
      })),
      
      // Totals
      subtotal: pickupData.products.reduce((sum, p) => sum + (p.price * p.quantity), 0),
      tax: 0,
      shipping: 0,
      total: pickupData.products.reduce((sum, p) => sum + (p.price * p.quantity), 0),
      
      // Order metadata
      status: 'Pending',
      paymentMethod: taskData.payment?.mode || 'cash',
      
      // Link to original task
      taskId: taskData.taskId,
      invoiceId: taskData.invoiceId,
      invoiceNumber: taskData.invoiceNumber,
      
      // Supplier order type identifier
      orderType: 'supplier_pickup', // This identifies it as a supplier order
      sourceVendorId: taskData.vendorId, // Who created the original task
      sourceVendorName: vendorProfile?.shopName || vendorProfile?.businessName || 'Vendor',
      
      // Pickup details
      pickupAddress: pickupData.supplierAddress,
      pickupLandmark: pickupData.supplierLandmark,
      
      // Delivery customer details
      deliveryCustomerName: taskData.customer?.name,
      deliveryCustomerPhone: taskData.customer?.phone,
      deliveryAddress: taskData.delivery?.address,
      
      // Status history
      statusHistory: [{
        status: 'Pending',
        timestamp: new Date(),
        note: 'Order received from ' + (vendorProfile?.shopName || 'Vendor')
      }],
      
      // Timestamps
      createdAt: Timestamp.fromDate(new Date()),
      updatedAt: Timestamp.fromDate(new Date())
    };

    const docRef = await addDoc(collection(db, 'orders'), orderData);
    console.log('✅ Supplier order created in orders collection:', docRef.id);

    // Also create a notification for real-time alert
    if (supplierId) {
      await addDoc(collection(db, 'notifications'), {
        vendorId: supplierId,
        type: 'new_order',
        title: 'New Pickup Order',
        message: `New pickup order from ${vendorProfile?.shopName || 'Vendor'} - ${pickupData.products.length} product(s)`,
        orderId: docRef.id,
        taskId: taskData.taskId,
        read: false,
        createdAt: Timestamp.fromDate(new Date()),
      });
    }

    return { id: docRef.id, ...orderData };
  } catch (error) {
    console.error('Error creating supplier order:', error);
    throw error;
  }
};